import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Col,
  Row,
  message,
  Select,
  Card,
  Space,
  Divider,
  Modal,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { API_METHODS, BLANK_VALUE } from "../../constants";
import useApi from "../../hooks/useApi";
import { useNavigate, useParams } from "react-router-dom";
import MetaTitleField from "./fields/MetaTitleField";
import MetaDescriptionField from "./fields/MetaDescriptionField";
import SchemaField from "./fields/SchemaField";
import SocialMediaFields from "./fields/SocialMediaFields";
import SitemapFields from "./fields/SitemapFields";

import { SEO_CONFIG, COMMON_FIELDS } from "./config/seoConfig";

const { TextArea } = Input;

const SeoForm = ({ entityType, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // Get configuration for this entity type
  const config = SEO_CONFIG[entityType];

  // Determine if this is edit mode and get entity ID
  const entityId = params.id;
  const isEdit = !!entityId;
  const isStaticPage = config.isStatic === true;

  // API hooks
  const [response, request] = useApi(
    config.apiEndpoint,
    null,
    API_METHODS.POST,
    true
  );

  const [editResponse, editRequest] = useApi(
    `${config.apiEndpoint}/${entityId}`,
    null,
    API_METHODS.PUT,
    true
  );

  const [getSeoResponse, getSeoRequest] = useApi(
    `${config.apiEndpoint}/${entityId}`,
    null,
    API_METHODS.GET
  );

  const [deleteResponse, deleteRequest] = useApi(
    `${config.apiEndpoint}/${entityId}`,
    null,
    API_METHODS.DELETE
  );

  const [entitiesResponse, entitiesRequest] = useApi(
    config.entityEndpoint,
    null,
    API_METHODS.GET
  );

  // State
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load data on component mount
  useEffect(() => {
    if (!isEdit && !isStaticPage) {
      // Load entities for dropdown in add mode (not needed for static pages)
      entitiesRequest();
    } else if (isEdit) {
      // Load existing SEO data in edit mode
      getSeoRequest();
    }
  }, []);

  // Handle entities dropdown data
  useEffect(() => {
    const data =
      entitiesResponse.data !== BLANK_VALUE ? entitiesResponse.data : [];
    setEntities(data);
  }, [entitiesResponse]);

  // Handle get SEO response (edit mode)
  useEffect(() => {
    if (getSeoResponse.data) {
      const seoData = getSeoResponse.data;

      // Handle additional_schema
      if (
        seoData.additional_schema &&
        typeof seoData.additional_schema === "object"
      ) {
        seoData.additional_schema = JSON.stringify(
          seoData.additional_schema,
          null,
          2
        );
      }

      // ✅ Handle existing images - convert URLs to file list format for Upload component
      if (seoData.og_image && typeof seoData.og_image === "string") {
        seoData.og_image = [
          {
            uid: "-1",
            name: "og_image.jpg",
            status: "done",
            url: seoData.og_image,
          },
        ];
      }

      if (seoData.twitter_image && typeof seoData.twitter_image === "string") {
        seoData.twitter_image = [
          {
            uid: "-2",
            name: "twitter_image.jpg",
            status: "done",
            url: seoData.twitter_image,
          },
        ];
      }

      form.setFieldsValue(seoData);
      setSelectedEntity(seoData);
    }
  }, [getSeoResponse]);

  // Handle add response
  useEffect(() => {
    if (response.error) {
      messageApi.open({
        type: "error",
        content: response.error,
      });
    } else if (response.data) {
      messageApi.open({
        type: "success",
        content: `${config.title} added successfully.`,
      });
      setTimeout(() => {
        onSuccess ? onSuccess() : navigate(config.navigationPath);
      }, 1000);
    }
  }, [response]);

  // Handle update response
  useEffect(() => {
    if (editResponse.error) {
      messageApi.open({
        type: "error",
        content: editResponse.error,
      });
    } else if (editResponse.data) {
      messageApi.open({
        type: "success",
        content: `${config.title} updated successfully.`,
      });
      setTimeout(() => {
        onSuccess ? onSuccess() : navigate(config.navigationPath);
      }, 1000);
    }
  }, [editResponse]);

  // Handle delete response
  useEffect(() => {
    if (deleteResponse.error && !deleteResponse.isLoading) {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
      messageApi.open({
        type: "error",
        content: "Error deleting SEO data",
      });
    } else if (deleteResponse.data && !deleteResponse.isLoading) {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
      messageApi.open({
        type: "success",
        content: `${config.title} deleted successfully`,
      });
      setTimeout(() => {
        onSuccess ? onSuccess() : navigate(config.navigationPath);
      }, 1000);
    }
  }, [deleteResponse]);

  // Form submission
  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        // Handle file uploads
        const formData = new FormData();

        // Add all text fields
        Object.keys(values).forEach((key) => {
          if (values[key] !== undefined && values[key] !== null) {
            if (key === "og_image" || key === "twitter_image") {
              // ✅ Only append NEW uploads (with originFileObj)
              if (
                Array.isArray(values[key]) &&
                values[key].length > 0 &&
                values[key][0].originFileObj
              ) {
                formData.append(
                  key,
                  values[key][0].originFileObj,
                  values[key][0].name
                );
              }
              // Existing images (with .url but no .originFileObj) - don't append
              // Backend will keep the existing image
            } else {
              formData.append(key, String(values[key]));
            }
          }
        });

        if (isEdit) {
          editRequest(formData);
        } else {
          request(formData);
        }
      })
      .catch((e) => {
        console.error("Validation failed:", e);
      });
  };

  // Handle delete
  const handleDelete = () => {
    setDeleteLoading(true);
    deleteRequest();
  };

  // Handle cancel
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(config.navigationPath);
    }
  };

  return (
    <Card
      title={isEdit ? `Update ${config.title}` : `Add ${config.title}`}
      extra={
        isEdit && (
          <Button
            danger
            onClick={() => setShowDeleteConfirm(true)}
            icon={<DeleteOutlined />}
          >
            Delete SEO
          </Button>
        )
      }
    >
      {contextHolder}

      <Form layout="vertical" form={form}>
        {/* Entity Selection/Display */}
        {!isEdit ? (
          isStaticPage ? (
            // Static pages - dropdown selection
            <>
              <Divider>
                <b>Page Information</b>
              </Divider>
              <Row gutter={16}>
                <Col lg={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="page_identifier"
                    label="Select Page"
                    rules={[
                      {
                        required: true,
                        message: "Please select a page",
                      },
                    ]}
                  >
                    <Select placeholder="Select a page" showSearch>
                      {config.staticPages?.map((identifier) => (
                        <Select.Option key={identifier} value={identifier}>
                          {identifier}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            // Other entities - dropdown selection
            <>
              <Divider>
                <b>Select {config.entityLabel}</b>
              </Divider>
              <Row gutter={16}>
                <Col lg={12} md={24} sm={24} xs={24}>
                  <Form.Item
                    name={config.entityIdField}
                    label={config.entityLabel}
                    rules={[
                      {
                        required: true,
                        message: `${config.entityLabel} is required.`,
                      },
                    ]}
                  >
                    <Select
                      placeholder={`Select ${config.entityLabel}`}
                      showSearch
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {entities?.map((entity) => (
                        <Select.Option key={entity.id} value={entity.id}>
                          {entity.name ||
                            entity.product_name ||
                            entity.subcategory ||
                            entity.category ||
                            entity.main_category}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )
        ) : (
          // Edit mode display
          <>
            <Divider>
              <b>
                {isStaticPage
                  ? "Editing Static Page SEO"
                  : `Editing SEO for ${config.entityLabel}`}
              </b>
            </Divider>
            <Row gutter={16}>
              {isStaticPage ? (
                <Col lg={12} md={24} sm={24} xs={24}>
                  <Form.Item name="page_identifier" label="Select Page">
                    <Select disabled style={{ backgroundColor: "#f5f5f5" }}>
                      {config.staticPages?.map((identifier) => (
                        <Select.Option key={identifier} value={identifier}>
                          {identifier}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              ) : (
                <Col lg={12} md={24} sm={24} xs={24}>
                  <Form.Item label={config.entityLabel}>
                    <Input
                      value={
                        selectedEntity?.name ||
                        selectedEntity?.product_name ||
                        selectedEntity?.subcategory ||
                        selectedEntity?.category ||
                        selectedEntity?.main_category ||
                        "Loading..."
                      }
                      disabled
                      style={{ backgroundColor: "#f5f5f5" }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </>
        )}

        {/* Basic SEO Details */}
        <Divider>
          <b>Basic SEO Details</b>
        </Divider>
        <Row gutter={16}>
          <Col lg={8} md={12} sm={24} xs={24}>
            <MetaTitleField required={true} />
          </Col>
          <Col lg={8} md={12} sm={24} xs={24}>
            <MetaDescriptionField required={true} />
          </Col>
          <Col lg={8} md={24} sm={24} xs={24}>
            <Form.Item name="meta_keywords" label="Meta Keywords">
              <TextArea placeholder="keyword1, keyword2, keyword3" rows={2} />
            </Form.Item>
          </Col>
        </Row>

        {/* Technical SEO */}
        <Divider>
          <b>Technical SEO</b>
        </Divider>
        <Row gutter={16}>
          <Col lg={8} md={12} sm={24} xs={24}>
            <Form.Item name="meta_robots" label="Meta Robots">
              <Select placeholder="Select robots directive">
                {COMMON_FIELDS.META_ROBOTS_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={8} md={12} sm={24} xs={24}>
            <Form.Item name="canonical_url" label="Canonical URL">
              <Input placeholder="https://example.com/canonical-url" />
            </Form.Item>
          </Col>
        </Row>

        {/* Social Media */}
        <Divider>
          <b>Social Media</b>
        </Divider>
        <SocialMediaFields />

        {/* Schema & Structure */}
        <Divider>
          <b>Schema & Structure</b>
        </Divider>
        <Row gutter={16}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <SchemaField />
          </Col>
        </Row>

        {/* Sitemap Settings */}
        <Divider>
          <b>Sitemap Settings</b>
        </Divider>
        <SitemapFields />

        {/* Action Buttons */}
        <Row style={{ marginTop: "24px" }}>
          <Col span={24}>
            <Space style={{ float: "right" }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={response.isLoading || editResponse.isLoading}
              >
                {isEdit ? "Update" : "Save"} SEO
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete SEO Data"
        open={showDeleteConfirm}
        onOk={handleDelete}
        okText="Yes"
        okButtonProps={{
          loading: deleteLoading,
          danger: true,
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      >
        Are you sure you want to delete this SEO data? This action cannot be
        undone.
      </Modal>
    </Card>
  );
};

export default SeoForm;
